import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { Check, ChevronRight } from 'lucide-react';
import {
  forwardRef,
  useId,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type HTMLAttributes,
  type ReactNode,
} from 'react';
import { cn } from './cn';

export const DropdownMenu = DropdownMenuPrimitive.Root;
/** Use `asChild` with a `Button` or `IconButton`. */
export const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
export const DropdownMenuGroup = DropdownMenuPrimitive.Group;
export const DropdownMenuSub = DropdownMenuPrimitive.Sub;
export const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

const CONTENT =
  'z-50 min-w-[12rem] max-w-[min(20rem,calc(100vw-1rem))] overflow-y-auto rounded-lg border border-border bg-surface-raised p-1 text-sm text-fg shadow-lg outline-none data-[state=closed]:animate-scale-out data-[state=open]:animate-scale-in';

const ITEM =
  'group relative flex min-h-8 cursor-default select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm text-fg outline-none transition-colors duration-100 data-[disabled]:pointer-events-none data-[highlighted]:bg-surface-muted data-[disabled]:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0';

export const DropdownMenuContent = /* @__PURE__ */ forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(function DropdownMenuContent(
  {
    className,
    align = 'start',
    sideOffset = 6,
    collisionPadding = 8,
    ...props
  },
  ref
) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        className={cn(
          CONTENT,
          'max-h-[var(--radix-dropdown-menu-content-available-height)] origin-[--radix-dropdown-menu-content-transform-origin]',
          className
        )}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
});

export interface DropdownMenuItemProps extends ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.Item
> {
  icon?: ReactNode;
  /** Keyboard shortcut hint shown at the end of the row. */
  shortcut?: string;
  /** Secondary line under the label. */
  description?: ReactNode;
  /** `danger` for destructive actions (the confirmation still uses a Dialog). */
  tone?: 'default' | 'danger';
  /**
   * Why the item is unavailable. With `disabled`, the item stays focusable,
   * shows the reason under its label, and ignores selection.
   */
  disabledReason?: ReactNode;
}

export const DropdownMenuItem = /* @__PURE__ */ forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Item>,
  DropdownMenuItemProps
>(function DropdownMenuItem(
  {
    className,
    icon,
    shortcut,
    description,
    tone = 'default',
    disabled,
    disabledReason,
    onSelect,
    children,
    ...props
  },
  ref
) {
  const descriptionId = useId();
  const softDisabled =
    Boolean(disabled) &&
    disabledReason !== undefined &&
    disabledReason !== null;
  const secondary = softDisabled ? disabledReason : description;
  return (
    <DropdownMenuPrimitive.Item
      ref={ref}
      disabled={Boolean(disabled) && !softDisabled}
      aria-disabled={softDisabled || undefined}
      aria-describedby={secondary != null ? descriptionId : undefined}
      onSelect={(event) => {
        if (softDisabled) {
          event.preventDefault();
          return;
        }
        onSelect?.(event);
      }}
      className={cn(
        ITEM,
        secondary != null && 'items-start',
        tone === 'danger'
          ? 'text-danger-fg data-[highlighted]:bg-danger-muted'
          : undefined,
        softDisabled && 'text-fg-muted',
        className
      )}
      {...props}
    >
      {icon != null && (
        <span
          aria-hidden="true"
          className={cn(
            'flex',
            tone === 'danger' ? 'text-danger-fg' : 'text-fg-muted',
            softDisabled && 'text-fg-subtle',
            secondary != null && 'mt-0.5'
          )}
        >
          {icon}
        </span>
      )}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate">{children}</span>
        {secondary != null && (
          <span
            id={descriptionId}
            className="text-xs leading-snug text-fg-muted"
          >
            {secondary}
          </span>
        )}
      </span>
      {shortcut && <DropdownMenuShortcut>{shortcut}</DropdownMenuShortcut>}
    </DropdownMenuPrimitive.Item>
  );
});

export const DropdownMenuCheckboxItem = /* @__PURE__ */ forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(function DropdownMenuCheckboxItem({ className, children, ...props }, ref) {
  return (
    <DropdownMenuPrimitive.CheckboxItem
      ref={ref}
      className={cn(ITEM, 'pl-8', className)}
      {...props}
    >
      <span className="absolute left-2 flex size-4 items-center justify-center">
        <DropdownMenuPrimitive.ItemIndicator>
          <Check className="text-accent" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  );
});

export const DropdownMenuRadioItem = /* @__PURE__ */ forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem> & {
    icon?: ReactNode;
  }
>(function DropdownMenuRadioItem({ className, children, icon, ...props }, ref) {
  return (
    <DropdownMenuPrimitive.RadioItem
      ref={ref}
      className={cn(ITEM, 'pl-8', className)}
      {...props}
    >
      <span className="absolute left-2 flex size-4 items-center justify-center">
        <DropdownMenuPrimitive.ItemIndicator>
          <Check className="text-accent" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {icon != null && (
        <span aria-hidden="true" className="flex text-fg-muted">
          {icon}
        </span>
      )}
      {children}
    </DropdownMenuPrimitive.RadioItem>
  );
});

export const DropdownMenuLabel = /* @__PURE__ */ forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Label>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label>
>(function DropdownMenuLabel({ className, ...props }, ref) {
  return (
    <DropdownMenuPrimitive.Label
      ref={ref}
      className={cn(
        'px-2 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wide text-fg-muted',
        className
      )}
      {...props}
    />
  );
});

export const DropdownMenuSeparator = /* @__PURE__ */ forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Separator>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(function DropdownMenuSeparator({ className, ...props }, ref) {
  return (
    <DropdownMenuPrimitive.Separator
      ref={ref}
      className={cn('-mx-1 my-1 h-px bg-border', className)}
      {...props}
    />
  );
});

export const DropdownMenuSubTrigger = /* @__PURE__ */ forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
    icon?: ReactNode;
  }
>(function DropdownMenuSubTrigger(
  { className, children, icon, ...props },
  ref
) {
  return (
    <DropdownMenuPrimitive.SubTrigger
      ref={ref}
      className={cn(ITEM, 'data-[state=open]:bg-surface-muted', className)}
      {...props}
    >
      {icon != null && (
        <span aria-hidden="true" className="flex text-fg-muted">
          {icon}
        </span>
      )}
      <span className="flex-1 truncate">{children}</span>
      <ChevronRight aria-hidden="true" className="ml-auto text-fg-muted" />
    </DropdownMenuPrimitive.SubTrigger>
  );
});

export const DropdownMenuSubContent = /* @__PURE__ */ forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(function DropdownMenuSubContent(
  { className, collisionPadding = 8, ...props },
  ref
) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.SubContent
        ref={ref}
        collisionPadding={collisionPadding}
        className={cn(
          CONTENT,
          'origin-[--radix-dropdown-menu-content-transform-origin]',
          className
        )}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
});

export const DropdownMenuShortcut = ({
  className,
  ...props
}: HTMLAttributes<HTMLSpanElement>) => (
  <span
    className={cn(
      'ml-auto pl-4 text-xs tracking-wide text-fg-muted',
      className
    )}
    {...props}
  />
);
